/**
 * Each room announces itself with a picture of what it does, drawn with the
 * same crayon the app draws with. No icon badges, no subtitle copy — a child
 * who cannot read still knows which one is which.
 */

import { useEffect, useRef } from 'preact/hooks';
import type { JSX } from 'preact';
import type { Stroke } from '../engine/stroke.ts';
import { drawStroke } from '../engine/render.ts';
import { token } from '../engine/palette.ts';

export type ArtKind = 'move' | 'numbers' | 'patterns' | 'shapes' | 'build' | 'music' | 'draw';

const MAX_DPR = 2;

export function RoomArt({ kind }: { kind: ArtKind }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let raf = 0;
    let start = performance.now();

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0) return;
      const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      const bw = Math.round(rect.width * dpr);
      const bh = Math.round(rect.height * dpr);
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      // The first frame can carry a timestamp from before this effect ran, so
      // the elapsed time comes out very slightly negative. Every painter here
      // treats t as an age, and a negative one indexes off the front of an
      // array — so it is clamped once, here, rather than defended against
      // five times over.
      const t = Math.max(0, (now - start) / 1000);
      if (t > 3600) start = now;
      PAINT[kind](ctx, t, rect.width, rect.height);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [kind]);

  return <canvas class="room__art" ref={ref} aria-hidden="true" />;
}

function crayon(points: { x: number; y: number }[], color: string, width: number, seed: number): Stroke {
  return { brush: 'crayon', color, width, points, speeds: points.map(() => 0), seed };
}

function ringPoints(cx: number, cy: number, rx: number, ry: number, wobble: number, seed: number) {
  const pts = [];
  for (let i = 0; i <= 22; i++) {
    const a = (i / 22) * Math.PI * 2;
    const r = 1 + Math.sin(a * 3 + seed) * wobble;
    pts.push({ x: cx + Math.cos(a) * rx * r, y: cy + Math.sin(a) * ry * r });
  }
  return pts;
}

const PAINT: Record<ArtKind, (ctx: CanvasRenderingContext2D, t: number, w: number, h: number) => void> = {
  /** A drawn blob dropping, squashing on the floor and springing back. */
  move: (ctx, t, w, h) => {
    const floor = h * 0.86;
    const period = 1.5;
    const phase = (t % period) / period;
    // Parabola: fast at the floor, slow at the top, like a real bounce.
    const height = 4 * phase * (1 - phase);
    const radius = w * 0.21;
    const cy = floor - radius - height * (h * 0.45);
    const squash = Math.max(0, 1 - height * 7);

    ctx.save();
    ctx.globalAlpha = 0.12 + squash * 0.14;
    ctx.fillStyle = token('--ink');
    ctx.beginPath();
    ctx.ellipse(w / 2, floor + 2, radius * (0.6 + squash * 0.5), radius * 0.16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    drawStroke(
      ctx,
      crayon(
        ringPoints(w / 2, cy, radius * (1 + squash * 0.16), radius * (1 - squash * 0.22), 0.05, 1),
        token('--crayon-purple'),
        Math.max(5, w * 0.075),
        7
      )
    );
  },

  /** Twelve blocks: a 3x4 rectangle with two that would not fit. */
  numbers: (ctx, t, w, h) => {
    const cols = 3;
    const rows = 3;
    // Three rows plus the leftover row have to fit inside the square.
    const pitch = Math.min(w * 0.21, h * 0.19);
    const cell = pitch * 0.82;
    const originX = w / 2 - (cols * pitch - (pitch - cell)) / 2;
    const originY = h * 0.1;

    const block = (x: number, y: number, color: string) => {
      ctx.beginPath();
      ctx.roundRect(x, y, cell, cell, cell * 0.28);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = Math.max(2, cell * 0.1);
      ctx.strokeStyle = token('--ink');
      ctx.stroke();
    };

    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        block(originX + c * pitch, originY + r * pitch, token('--crayon-teal'));
      }
    }
    const bob = Math.sin(t * 2.4) * cell * 0.09;
    block(originX + pitch * 0.5, originY + rows * pitch + cell * 0.45 + bob, token('--crayon-orange'));
    block(originX + pitch * 1.5, originY + rows * pitch + cell * 0.45 - bob, token('--crayon-orange'));
  },

  /**
   * A wheel with a hand going round it, leaving a star behind. Whichever
   * pattern toy a child opens, this is what they are doing: going round
   * something and watching what shape it leaves.
   */
  patterns: (ctx, t, w, h) => {
    const cx = w / 2;
    const cy = h * 0.5;
    const radius = Math.min(w, h) * 0.34;
    const places = 8;
    const step = 3;
    const hops = 1 + Math.floor(t * 1.4);

    const pointAt = (place: number, r = radius) => {
      const a = ((place % places) / places) * Math.PI * 2 - Math.PI / 2;
      return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
    };

    ctx.save();
    ctx.strokeStyle = token('--paper-line');
    ctx.lineWidth = Math.max(2, w * 0.018);
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();

    // The star the hop leaves behind, drawn on as it goes.
    ctx.strokeStyle = token('--crayon-purple');
    ctx.lineWidth = Math.max(3, w * 0.032);
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 1; i <= Math.min(hops, places); i++) {
      const from = pointAt((i - 1) * step);
      const to = pointAt(i * step);
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
    }
    ctx.stroke();

    for (let place = 0; place < places; place++) {
      const p = pointAt(place);
      ctx.fillStyle = token('--crayon-teal');
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(3, w * 0.035), 0, Math.PI * 2);
      ctx.fill();
    }

    // The hand.
    const tip = pointAt((hops * step) % places, radius * 0.78);
    ctx.strokeStyle = token('--crayon-red');
    ctx.lineWidth = Math.max(3, w * 0.03);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(tip.x, tip.y);
    ctx.stroke();
    ctx.restore();
  },

  /** A little stack of blocks seen from the corner, building itself up. */
  build: (ctx, t, w, h) => {
    const hw = w * 0.14;
    const hd = w * 0.075;
    const rise = w * 0.16;
    const cx = w / 2;
    const cy = h * 0.42;
    // Where each block sits: two on the ground, then one on top.
    const stack = [
      { x: 0, y: 0, z: 0, color: '--crayon-red' },
      { x: 1, y: 0, z: 0, color: '--crayon-yellow' },
      { x: 0, y: 0, z: 1, color: '--crayon-sky' },
      { x: 0, y: 1, z: 0, color: '--crayon-teal' },
    ];
    const shown = 1 + (Math.floor(t / 0.7) % stack.length);

    // Far blocks first, then higher ones — the same order the real room uses.
    const order = [...stack.slice(0, shown)].sort((a, b) => a.x + a.z - (b.x + b.z) || a.y - b.y);
    for (const block of order) {
      const px = cx + (block.x - block.z) * hw;
      const py = cy + (block.x + block.z) * hd - block.y * rise;
      const face = (points: Array<[number, number]>, tint: number) => {
        ctx.beginPath();
        ctx.moveTo(px + points[0][0], py + points[0][1]);
        for (const [dx, dy] of points.slice(1)) ctx.lineTo(px + dx, py + dy);
        ctx.closePath();
        ctx.fillStyle = `color-mix(in srgb, ${token(block.color)} ${Math.round((1 - Math.abs(tint)) * 100)}%, ${tint > 0 ? '#ffffff' : '#241d3a'})`;
        ctx.fill();
        ctx.lineWidth = Math.max(2, w * 0.02);
        ctx.strokeStyle = 'rgba(44, 37, 69, 0.85)';
        ctx.lineJoin = 'round';
        ctx.stroke();
      };
      face([[-hw, 0], [0, hd], [0, hd + rise], [-hw, rise]], -0.28);
      face([[hw, 0], [0, hd], [0, hd + rise], [hw, rise]], -0.14);
      face([[0, -hd], [hw, 0], [0, hd], [-hw, 0]], 0.14);
    }
  },

  /**
   * A drawn shape that keeps growing a corner — triangle, square, pentagon —
   * and rocks on its flat side, which is the whole room in one picture.
   */
  shapes: (ctx, t, w, h) => {
    const sides = 3 + (Math.floor(t / 1.6) % 6);
    const phase = (t / 1.6) % 1;
    const radius = Math.min(w, h) * 0.3;
    const cx = w / 2;
    const cy = h * 0.52;
    const rock = Math.sin(t * 2.2) * (0.06 + 0.5 / sides);

    ctx.save();
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = token('--ink');
    ctx.beginPath();
    ctx.ellipse(cx, cy + radius * 1.06, radius * 0.8, radius * 0.11, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    const pts = [];
    for (let i = 0; i <= sides; i++) {
      // Start on a flat edge so the shape sits down rather than on a point.
      const a = (i / sides) * Math.PI * 2 + Math.PI / 2 + Math.PI / sides + rock;
      pts.push({ x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius });
    }
    const grow = 0.85 + Math.min(1, phase * 6) * 0.15;
    drawStroke(
      ctx,
      crayon(
        pts.map((p) => ({ x: cx + (p.x - cx) * grow, y: cy + (p.y - cy) * grow })),
        token('--crayon-sky'),
        Math.max(5, w * 0.07),
        sides * 5
      )
    );
  },

  /**
   * Three drawn lines of different lengths with a ball bouncing along them —
   * the room in one picture: what you draw is what it sounds like.
   */
  music: (ctx, t, w, h) => {
    const bars = [
      { y: 0.34, from: 0.14, to: 0.62, color: token('--crayon-purple') },
      { y: 0.56, from: 0.3, to: 0.9, color: token('--crayon-teal') },
      { y: 0.78, from: 0.1, to: 0.5, color: token('--crayon-orange') },
    ];
    // Which bar the ball is on right now, and how far along it has got.
    const period = 1.35;
    const index = ((Math.floor(t / period) % bars.length) + bars.length) % bars.length;
    const phase = (t % period) / period;

    bars.forEach((bar, i) => {
      const struck = i === index && phase < 0.3;
      ctx.save();
      if (struck) {
        ctx.shadowColor = bar.color;
        ctx.shadowBlur = 14;
      }
      drawStroke(
        ctx,
        crayon(
          [
            { x: w * bar.from, y: h * bar.y },
            { x: w * bar.to, y: h * (bar.y + 0.02) },
          ],
          bar.color,
          Math.max(5, w * 0.06),
          i * 7 + 2
        )
      );
      ctx.restore();
    });

    const bar = bars[index];
    const along = bar.from + (bar.to - bar.from) * Math.min(1, phase * 1.6);
    const hop = Math.abs(Math.sin(phase * Math.PI * 3)) * h * 0.13;
    ctx.save();
    ctx.fillStyle = token('--crayon-yellow');
    ctx.strokeStyle = 'rgba(44, 37, 69, 0.8)';
    ctx.lineWidth = Math.max(2, w * 0.02);
    ctx.beginPath();
    ctx.arc(w * along, h * bar.y - hop - w * 0.05, Math.max(5, w * 0.055), 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  },

  /** Three crayon strokes that draw themselves, over and over. */
  draw: (ctx, t, w, h) => {
    const lines = [
      { color: token('--crayon-red'), y: 0.3, seed: 3 },
      { color: token('--crayon-sky'), y: 0.53, seed: 11 },
      { color: token('--crayon-yellow'), y: 0.76, seed: 21 },
    ];
    // Draw all three, then hold the finished picture for a beat before looping.
    const cycle = (t % 6) / 6;
    lines.forEach((line, i) => {
      const progress = Math.max(0, Math.min(1, cycle * 4 - i * 0.6));
      if (progress <= 0) return;
      const pts = [];
      const steps = Math.max(2, Math.round(20 * progress));
      for (let s = 0; s <= steps; s++) {
        const p = (s / 20) * progress + 0;
        pts.push({
          x: w * (0.14 + p * 0.72),
          y: h * line.y + Math.sin(p * 9 + line.seed) * h * 0.06,
        });
      }
      drawStroke(ctx, crayon(pts, line.color, Math.max(5, w * 0.075), line.seed));
    });
  },
};

/** A hand-drawn underline for the wordmark — a straight rule would look bought. */
export function CrayonRule(): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx || rect.width === 0) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const pts = [];
    for (let i = 0; i <= 26; i++) {
      const p = i / 26;
      pts.push({ x: rect.width * (0.01 + p * 0.98), y: rect.height * 0.55 + Math.sin(p * 5.5) * rect.height * 0.18 });
    }
    drawStroke(ctx, crayon(pts, token('--crayon-orange'), rect.height * 0.4, 5));
  }, []);

  return <canvas class="wordmark__rule" ref={ref} aria-hidden="true" />;
}
